import { Button, Caption1, MessageBar, MessageBarBody, Text, makeStyles } from '@fluentui/react-components';
import type { Person } from '@vigeo/shared';
import { StatusDot, complianceTokenFor, tokens } from '@vigeo/ui';
import { StoredFileLink } from '@/features/files';
import { OfferTracker } from './OfferTracker.js';
import { moveCopy, offerTrackerSteps, outcomeCopy, outcomeLede, sentCopy, stageSteps, stageReachedFor, type SignedOfferSummary } from './model.js';

/**
 * WHAT "OFFER AND STAGE" SHOWS ONCE THE MOVE HAS ALREADY HAPPENED (#346).
 *
 * 2.1 and 2.2 both stop applying the moment a candidate becomes Onboarding — correctly, because
 * there is nothing left to approve or move. Until now the section then rendered nothing, and what
 * showed instead was the older workflow panel and a general-purpose stage dropdown, neither of
 * which appears anywhere in the frames. The screen looked like a different design.
 *
 * DESIGNED HERE, NOT TAKEN FROM A FRAME. Paul's frames end at the move, so this state had no
 * drawing. It is built from 2.2's own parts — the same card, the same offer row, the same stage
 * strip — so it reads as the end of that screen rather than as a new one, and so a later frame can
 * replace it as copy rather than as a rebuild.
 *
 * READ-ONLY ON PURPOSE. The offer is history at this point and the next move belongs to
 * "Orientation and start", where the stage control now lives. A second control here would be two
 * places to do one thing.
 *
 * It does NOT print the reason the mover typed. That is on the audit trail, and the web app has no
 * audit query — `['audit', personId]` is invalidated in three places and fetched in none — so
 * showing it would mean adding a fetch for one line. The timeline in History already holds it, so
 * this points there instead of duplicating it.
 */

const useStyles = makeStyles({
  card: {
    display: 'flex', flexDirection: 'column', minWidth: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    overflow: 'hidden',
  },
  intro: {
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS,
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalXL}`,
  },
  h: { margin: 0, fontSize: tokens.fontSizeBase600, lineHeight: tokens.lineHeightBase600, fontWeight: tokens.fontWeightSemibold },
  row: {
    display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalXL,
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalXL}`,
    borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
  },
  labelColumn: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, flex: '0 0 12.5rem', minWidth: 0 },
  rowLabel: { fontWeight: tokens.fontWeightSemibold },
  fields: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS, flex: '1 1 20rem', minWidth: 0 },
  statusLine: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: tokens.spacingHorizontalS },
  values: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS },
  muted: { color: tokens.colorNeutralForeground3 },
  banner: {
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS,
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalXL}`,
    borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
  },
  bannerActions: { display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: tokens.spacingHorizontalS },
  fileLine: { display: 'flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: tokens.spacingHorizontalS, minWidth: 0 },
});

export function OfferOutcome({ person, offer, firstName, hireDateLine, onResendWelcome, resending, onGoToDocuments }: {
  readonly person: Person;
  readonly offer: SignedOfferSummary | null;
  readonly firstName: string;
  /**
   * "Hired on {date}" or the not-recorded line. Formatted by the caller, which already owns this
   * screen's date formatters — this component takes no clock and no locale of its own, the same
   * reason `signedOfferSummary` is handed its formatters.
   */
  readonly hireDateLine: string;
  /** Undefined for a viewer, or where no Welcome template exists to send. */
  readonly onResendWelcome?: () => void;
  readonly resending?: boolean;
  readonly onGoToDocuments?: () => void;
}) {
  const styles = useStyles();
  const reached = stageReachedFor(person.lifecycle);

  return (
    <section className={styles.card} aria-label={moveCopy.title}>
      <div className={styles.intro}>
        <h3 className={styles.h}>{moveCopy.title}</h3>
        <Text>{outcomeLede(firstName, person.lifecycle)}</Text>
        <Caption1 className={styles.muted}>{hireDateLine}</Caption1>
      </div>

      {/*
        2.5's "sent" banner — the DURABLE counterpart to 2.4. 2.4 confirms a send to whoever
        performed it and then goes away; this says the same fact to anyone opening the record later,
        which is why 2.4 can be transient and this cannot.

        Shown only for `onboarding`: a withdrawn or separated person's record should not carry a live
        "resend their welcome email" button, and `outcomeLede` already speaks to those states.
      */}
      {person.lifecycle !== 'onboarding' ? null : (
        <div className={styles.banner}>
          <MessageBar intent="success">
            <MessageBarBody>{outcomeCopy.sentBanner}</MessageBarBody>
          </MessageBar>
          <div className={styles.bannerActions}>
            {onResendWelcome === undefined ? null : (
              <Button appearance="secondary" disabled={resending === true} onClick={onResendWelcome}>
                {resending === true ? outcomeCopy.resending : outcomeCopy.resendWelcome}
              </Button>
            )}
            {onGoToDocuments === undefined ? null : (
              <Button appearance="primary" onClick={onGoToDocuments}>{sentCopy.goToDocuments}</Button>
            )}
          </div>
          {/*
            NO SUCCESS LINE HERE. The section's own notice already says it, in a live region, and
            rendering both put the same sentence on screen twice — the third time this feature has
            done that, and the first two are recorded in `OfferStageSection`'s comments. The notice
            is also the one that clears on the next attempt; this one had no way to.
          */}
        </div>
      )}

      {offer === null ? null : (
        <div className={styles.row}>
          <div className={styles.labelColumn}>
            <Text className={styles.rowLabel}>{moveCopy.offerLabel}</Text>
            <Caption1 className={styles.muted}>{moveCopy.offerCaption}</Caption1>
          </div>
          <div className={styles.fields}>
            <div className={styles.statusLine}>
              {offer.badgeLabel === null ? null : (
                <StatusDot color={offer.badgeTone === 'good' ? complianceTokenFor.good : complianceTokenFor.warning}>{offer.badgeLabel}</StatusDot>
              )}
              {offer.approvedLine === null ? null : <Caption1 className={styles.muted}>{offer.approvedLine}</Caption1>}
            </div>
            <OfferTracker steps={offerTrackerSteps} reached={offerTrackerSteps.length - 1} label="Offer progress" tickReached />
            {offer.position === null ? null : (
              <div className={styles.values}>
                <Text>{offer.position}</Text>
                {offer.proposedStart === null ? null : <Caption1 className={styles.muted}>{outcomeCopy.proposedStart} {offer.proposedStart}</Caption1>}
              </div>
            )}
            {offer.signedLine === null ? null : <Caption1 className={styles.muted}>{offer.signedLine}</Caption1>}
            {/*
              The document itself, beside the terms it agreed. `StoredFileLink` renders nothing when
              there is neither a committed file nor a legacy URL, so a paper-recorded offer with no
              scan shows its terms without a dead link.
            */}
            <div className={styles.fileLine}>
              <StoredFileLink
                personId={person.id}
                storedFileId={offer.storedFileId}
                legacyUrl={offer.legacyUrl}
                label={outcomeCopy.viewSignedOffer}
              />
            </div>
          </div>
        </div>
      )}

      <div className={styles.row}>
        <div className={styles.labelColumn}>
          <Text className={styles.rowLabel}>{outcomeCopy.hiringProgressLabel}</Text>
          <Caption1 className={styles.muted}>{outcomeCopy.hiringProgressCaption}</Caption1>
        </div>
        <div className={styles.fields}>
          <OfferTracker
            steps={stageSteps.map((step) => step.label)}
            captions={stageSteps.map((step) => step.caption)}
            reached={reached}
            label={outcomeCopy.hiringProgressLabel}
            tickReached
          />
          <Caption1 className={styles.muted}>{outcomeCopy.whereTheReasonIs}</Caption1>
          <Caption1 className={styles.muted}>{outcomeCopy.whereTheNextMoveIs}</Caption1>
        </div>
      </div>
    </section>
  );
}
